import type { HybridKeyWraps } from './HybridBackupCrypto';
import { b64ToBytes, bytesToB64 } from './HybridBackupCrypto';

/** Configuration locale de la sauvegarde chiffrée. Aucun secret en clair (hors DEK, stockée à part). */
export interface HybridStoredConfig {
  version: 1;
  /** Enveloppes de clé utilisées pour les sauvegardes. null = configuration requise. */
  active: HybridKeyWraps | null;
  /** Enveloppes en attente : la clé de récupération a été affichée mais pas encore confirmée conservée. */
  pending: HybridKeyWraps | null;
  configured_at: string | null;
}

export interface HybridKeyMaterial {
  /** Clé de données AES-256 de cette installation (null tant que rien n'est configuré). */
  dek: Uint8Array | null;
  config: HybridStoredConfig | null;
}

/**
 * Port du coffre sécurisé du poste (safeStorage d'Electron en production).
 * La DEK ne quitte jamais le poste ; le serveur ne la voit pas.
 */
export interface HybridKeyStore {
  isAvailable(): Promise<boolean>;
  load(): Promise<HybridKeyMaterial>;
  save(material: { dek: Uint8Array; config: HybridStoredConfig }): Promise<void>;
  clear(): Promise<void>;
}

/** Implémentation mémoire (tests, repli sans coffre : la configuration ne survit pas). */
export class MemoryHybridKeyStore implements HybridKeyStore {
  private material: HybridKeyMaterial = { dek: null, config: null };

  constructor(private readonly available = true) {}

  async isAvailable(): Promise<boolean> {
    return this.available;
  }

  async load(): Promise<HybridKeyMaterial> {
    return {
      dek: this.material.dek ? new Uint8Array(this.material.dek) : null,
      config: this.material.config ? (JSON.parse(JSON.stringify(this.material.config)) as HybridStoredConfig) : null,
    };
  }

  async save(material: { dek: Uint8Array; config: HybridStoredConfig }): Promise<void> {
    this.material = {
      dek: new Uint8Array(material.dek),
      config: JSON.parse(JSON.stringify(material.config)) as HybridStoredConfig,
    };
  }

  async clear(): Promise<void> {
    this.material = { dek: null, config: null };
  }
}

/** Coffre sécurisé Electron via les canaux IPC validés de electron/hybrid-ipc.cjs. */
export class ElectronHybridKeyStore implements HybridKeyStore {
  private api() {
    return window.electron;
  }

  async isAvailable(): Promise<boolean> {
    const api = this.api();
    if (!api?.hybridKeyLoad) return false;
    return (await api.isSecureStorageAvailable?.()) ?? false;
  }

  async load(): Promise<HybridKeyMaterial> {
    const res = await this.api()?.hybridKeyLoad?.();
    if (!res) return { dek: null, config: null };
    return {
      dek: res.dek_b64 ? b64ToBytes(res.dek_b64) : null,
      config: (res.config as HybridStoredConfig | null) ?? null,
    };
  }

  async save(material: { dek: Uint8Array; config: HybridStoredConfig }): Promise<void> {
    const fn = this.api()?.hybridKeySave;
    if (!fn) throw new Error('Coffre sécurisé indisponible (hors Electron)');
    await fn({ dek_b64: bytesToB64(material.dek), config: material.config });
  }

  async clear(): Promise<void> {
    await this.api()?.hybridKeyClear?.();
  }
}

let current: HybridKeyStore | null = null;

export function getHybridKeyStore(): HybridKeyStore {
  current ??= new ElectronHybridKeyStore();
  return current;
}

/** Injection pour les tests. */
export function setHybridKeyStore(store: HybridKeyStore | null): void {
  current = store;
}
